import React from 'react';
import { useTranslation } from 'react-i18next';
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import cn from 'clsx';
import Button, { ButtonTooltip } from '@components-clean/core/button';
import { SubjectMonitoringReviewDto } from '@interfaces-clean/reviews.interface';
import { useSMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type Props = GridCustomCellProps & {
    studyRef: string;
    smStudyMemberId: string | undefined;
    canEdit: boolean;
    enterReviewMode: (reviewRecordId: number, sessionId: string) => void;
};

const EnterActiveReviewModeGridCell = ({
    dataItem,
    studyRef,
    smStudyMemberId,
    canEdit,
    enterReviewMode,
    tdProps,
}: Props) => {
    const review = dataItem as SubjectMonitoringReviewDto;
    const openReviewMutation = SubjectMonitoringReviewAPI.useOpenReviewMutation(studyRef);
    const smActiveReviewContext = useSMActiveReviewContext();
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit' });

    const reviewerIdsSet = new Set(review.reviewerIds);
    const userInReviewersList = smStudyMemberId != null && reviewerIdsSet.has(smStudyMemberId);

    const inReviewMode = smActiveReviewContext.state.reviewRecordId != null;

    // Can enter edit mode if
    // - review is open
    // - user has ReadWrite permission
    // - user is in the reviewers list for the review
    // - user is not entering review mode (mutation is running)
    // - user is not already in review mode
    const disabled =
        review.reviewed || !canEdit || !userInReviewersList || openReviewMutation.isMutating || inReviewMode;

    let tooltip: ButtonTooltip | undefined;
    if (inReviewMode) {
        tooltip = { text: t('manageReviewsGrid.actions.openReview.tooltips.alreadyInReviewMode') };
    } else if (openReviewMutation.isMutating) {
        tooltip = { text: t('manageReviewsGrid.actions.openReview.tooltips.enteringReviewMode') };
    }

    return (
        // Only positioning props from tdProps — spreading tdProps attaches Kendo handlers that swallow the click.
        <td style={tdProps?.style} className={cn(tdProps?.className)} colSpan={tdProps?.colSpan}>
            {review.reviewed ? (
                t('manageReviewsGrid.actions.openReview.closedReview')
            ) : !userInReviewersList ? (
                t('manageReviewsGrid.actions.openReview.reviewPending')
            ) : (
                <Button
                    data-testid={`enter-review-edit-mode-button-${review.recordId}`}
                    primary
                    disabled={disabled}
                    tooltip={tooltip}
                    onClick={async () => {
                        if (disabled) {
                            return;
                        }

                        if (review.recordId != null) {
                            const sessionId = await openReviewMutation.trigger(review.recordId);

                            enterReviewMode(review.recordId, sessionId);
                        }
                    }}
                >
                    {t('manageReviewsGrid.actions.openReview.text')}
                </Button>
            )}
        </td>
    );
};

export default EnterActiveReviewModeGridCell;
